import React, { useEffect, useRef } from "react";

/**
 * Lightweight Cloudflare Turnstile widget wrapper.
 *
 * - Loads the Cloudflare script once per page (idempotent).
 * - Calls `onVerify(token)` when the user (or managed-challenge flow) gets a token.
 * - Calls `onExpire()` / `onError()` so the parent form can clear its stored token
 *   and re-disable its Submit button.
 *
 * If REACT_APP_TURNSTILE_SITE_KEY is not set, this component renders nothing and
 * immediately reports a fake "skip" token so local development without Turnstile
 * still works — the backend also fails-open when unconfigured.
 *
 * Env: REACT_APP_TURNSTILE_SITE_KEY
 */
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function ensureScriptLoaded() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstilePromise) return window.__turnstilePromise;
  window.__turnstilePromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", resolve);
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(s);
  });
  return window.__turnstilePromise;
}

export default function TurnstileWidget({ onVerify, onExpire, onError, theme = "light", testId = "turnstile-widget" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey) {
      // Unconfigured — emit a sentinel token so the form can still submit.
      onVerify?.("__turnstile_skip__");
      return () => {};
    }
    let cancelled = false;
    ensureScriptLoaded()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (tok) => onVerify?.(tok),
          "expired-callback": () => onExpire?.(),
          "error-callback": () => onError?.(),
        });
      })
      .catch((e) => {
        console.error(e);
        onError?.();
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!siteKey) return null;
  return <div ref={containerRef} data-testid={testId} />;
}
